// Curated bilingual "how-to" knowledge base for the in-app Help Assistant.
// Single source of truth — hand-written and grounded in the real UI/permissions,
// so the assistant never invents steps. Add an entry here to teach a new task.

export type HowToRole = "ADMIN" | "BOARD_MEMBER" | "RESIDENT";

export interface HowToStep {
  es: string;
  en: string;
}

export interface HowTo {
  id: string;
  category: "governance" | "finance" | "resident" | "documents" | "security" | "account";
  /** Roles that can actually perform the task. Others see it greyed with a role tag. */
  roles: HowToRole[];
  /** Deep link to the relevant page (optional). */
  route?: string;
  titleEs: string;
  titleEn: string;
  /** ES + EN synonyms / colloquial terms used by the matcher. Lowercase. */
  keywords: string[];
  whoEs: string;
  whoEn: string;
  steps: HowToStep[];
  noteEs?: string;
  noteEn?: string;
}

export const HOWTOS: HowTo[] = [
  {
    id: "monthly-close",
    category: "finance",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/finance/close",
    titleEs: "Publicar el cierre financiero mensual",
    titleEn: "Publish the monthly financial close",
    keywords: [
      "cierre", "mensual", "publicar", "contable", "contador", "informe", "finanzas", "resultado",
      "close", "monthly", "publish", "bookkeeper", "accountant", "p&l", "report", "summary",
      "ingresos", "gastos", "reserva",
    ],
    whoEs: "Administradores, Junta o usuarios con el permiso «Gestionar finanzas» (contable)",
    whoEn: "Admins, board members, or users with the “Manage finances” (bookkeeper) permission",
    steps: [
      { es: "En el menú lateral, abra «Cierre Mensual».", en: "In the sidebar, open “Monthly Close”." },
      { es: "Elija el mes y año a cerrar (viene prellenado con el mes anterior).", en: "Pick the month and year to close (pre-filled with the previous month)." },
      { es: "Suba el informe P&L en PDF (desde su computadora o desde Google Drive).", en: "Upload the P&L report as a PDF (from your computer or Google Drive)." },
      { es: "Anote los ingresos totales, los gastos totales y, opcionalmente, el fondo de reserva. El resultado neto se calcula solo.", en: "Enter total income, total expenses, and optionally the reserve fund balance. The net result is computed automatically." },
      { es: "Use «Guardar borrador» para seguir después, o «Publicar» para hacerlo visible a todos los propietarios.", en: "Use “Save draft” to continue later, or “Publish” to make it visible to all owners." },
    ],
    noteEs: "El PDF publicado también aparece en «Informes». Puede despublicar un mes desde la tabla inferior si necesita corregirlo. Un administrador puede otorgar el permiso «Gestionar finanzas» al contable en «Usuarios».",
    noteEn: "The published PDF also appears under “Reports”. You can unpublish a month from the table below if it needs corrections. An admin can grant the “Manage finances” permission to the bookkeeper under “Users”.",
  },
  {
    id: "resident-view-finances",
    category: "finance",
    roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"],
    route: "/resident/finances",
    titleEs: "Ver las finanzas del condominio",
    titleEn: "View the condominium finances",
    keywords: [
      "finanzas", "informe", "mensual", "ingresos", "gastos", "reserva", "resultado", "transparencia",
      "finances", "report", "monthly", "income", "expenses", "reserve", "download", "descargar", "pdf",
    ],
    whoEs: "Todos los usuarios",
    whoEn: "All users",
    steps: [
      { es: "En el menú lateral, abra «Finanzas».", en: "In the sidebar, open “Finances”." },
      { es: "Vea las cifras del último mes publicado: ingresos, gastos, resultado y fondo de reserva.", en: "See the latest published month's figures: income, expenses, result, and reserve fund." },
      { es: "Use «Descargar informe (PDF)» para ver el informe completo del contador.", en: "Use “Download report (PDF)” to open the accountant's full report." },
      { es: "En «Meses anteriores» puede consultar los informes previos.", en: "Under “Previous months” you can browse earlier reports." },
    ],
    noteEs: "Solo se muestran los meses que la administración ha publicado.",
    noteEn: "Only months published by the administration are shown.",
  },
  {
    id: "create-meeting",
    category: "governance",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/meetings",
    titleEs: "Crear una reunión o evento de la Junta",
    titleEn: "Create a board meeting or event",
    keywords: [
      "reunion", "reuniones", "junta", "asamblea", "evento", "eventos", "cita", "agendar",
      "meeting", "meetings", "event", "events", "schedule", "calendar", "calendario", "board",
    ],
    whoEs: "Administradores o miembros de la Junta",
    whoEn: "Admins or board members",
    steps: [
      { es: "En el menú lateral, abra «Reuniones».", en: "In the sidebar, open “Meetings”." },
      { es: "Haga clic en el botón «+ Nueva Reunión».", en: "Click the “+ New Meeting” button." },
      { es: "Complete: título, tipo (Junta, Asamblea o Extraordinaria), fecha y hora, lugar y, si aplica, el enlace de videollamada.", en: "Fill in: title, type (Board, Assembly, or Extraordinary), date and time, location, and an optional video-call link." },
      { es: "Haga clic en «Guardar».", en: "Click “Save”." },
    ],
    noteEs: "La reunión queda visible automáticamente para todos los administradores y miembros de la Junta, y se publica en el Calendario de Google compartido. Los residentes no ven las reuniones.",
    noteEn: "The meeting is automatically visible to all admins and board members, and it’s published to the shared Google Calendar. Residents do not see meetings.",
  },
  {
    id: "publish-announcement",
    category: "resident",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/resident/announcements",
    titleEs: "Publicar un comunicado",
    titleEn: "Post an announcement",
    keywords: [
      "comunicado", "comunicados", "anuncio", "anuncios", "aviso", "notificar", "publicar",
      "announcement", "announcements", "notice", "post", "publish", "residents", "residentes",
      "correo", "email", "enviar", "send",
    ],
    whoEs: "Administradores o miembros de la Junta",
    whoEn: "Admins or board members",
    steps: [
      { es: "En el menú lateral, abra «Comunicados».", en: "In the sidebar, open “Announcements”." },
      { es: "Haga clic en «+ Nuevo Comunicado».", en: "Click “+ New Announcement”." },
      { es: "Complete: título, categoría, contenido y, opcionalmente, las fechas de vigencia. Marque «Mostrar en la página pública» solo si cualquier persona puede verlo.", en: "Fill in: title, category, content, and optional validity dates. Tick “Show on the public page” only if anyone may see it." },
      { es: "Haga clic en «Guardar» — se guarda como borrador.", en: "Click “Save” — it’s stored as a draft." },
      { es: "En la lista, use «Enviar / Publicar» para elegir las unidades que lo reciben por correo y, si marca «Publicar», verlo en el portal.", en: "From the list, use “Send / Publish” to choose the units that get it by email and, if you tick “Publish”, see it in the portal." },
    ],
    noteEs: "En el portal, un comunicado publicado lo ven solo las unidades a las que se envió (todas, si se envió a todas), además de la Administración y la Junta. La página pública solo muestra los marcados para ella.",
    noteEn: "In the portal, a published announcement is seen only by the units it was sent to (everyone, if sent to all), plus the administration and the Board. The public page shows only those ticked for it.",
  },
  {
    id: "report-maintenance",
    category: "resident",
    roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"],
    route: "/resident/maintenance",
    titleEs: "Reportar un trabajo o mantenimiento por hacer",
    titleEn: "Report building work or maintenance needed",
    keywords: [
      "mantenimiento", "trabajo", "trabajos", "reparar", "reparacion", "arreglar", "arreglo",
      "danado", "roto", "ascensor", "plomeria", "electrico", "area comun", "edificio", "obra",
      "maintenance", "work", "repair", "fix", "broken", "damaged", "elevator", "plumbing",
      "electrical", "common area", "building", "request",
    ],
    whoEs: "Cualquier usuario (residentes, Junta o administradores)",
    whoEn: "Any user (residents, board, or admins)",
    steps: [
      { es: "En el menú lateral, abra «Mantenimiento».", en: "In the sidebar, open “Maintenance”." },
      { es: "Haga clic en «+ Nueva Solicitud».", en: "Click “+ New Request”." },
      { es: "Elija la categoría (plomería, eléctrico, estructural, área común, ascensor, seguridad u otro).", en: "Choose the category (plumbing, electrical, structural, common area, elevator, security, or other)." },
      { es: "Escriba el título, la descripción y la prioridad; adjunte fotos si las tiene.", en: "Enter a title, description, and priority; attach photos if you have them." },
      { es: "Haga clic en «Guardar». Un administrador asignará al personal y actualizará el estado.", en: "Click “Save”. An admin will assign staff and update the status." },
    ],
  },
  {
    id: "submit-works-permit",
    category: "resident",
    roles: ["RESIDENT", "ADMIN", "BOARD_MEMBER"],
    route: "/resident/works-permit",
    titleEs: "Solicitar un permiso de obra",
    titleEn: "Request a works permit",
    keywords: [
      "permiso", "permisos", "obra", "obras", "remodelacion", "remodelar", "construccion",
      "renovacion", "contratista", "permit", "works", "remodel", "construction", "renovation",
      "contractor",
    ],
    whoEs: "Residentes con una unidad asignada",
    whoEn: "Residents with an assigned unit",
    steps: [
      { es: "En el menú lateral, abra «Solicitudes de Titulares».", en: "In the sidebar, open “Works Permits”." },
      { es: "Haga clic en «+ Nueva Solicitud».", en: "Click “+ New Request”." },
      { es: "Complete: tipo de obra, descripción, fecha de inicio y fin estimada y, opcionalmente, el contratista y planos/archivos.", en: "Fill in: work type, description, start and estimated end dates, and optionally the contractor and plans/files." },
      { es: "Haga clic en «Guardar». La Junta revisará y aprobará o rechazará la solicitud.", en: "Click “Save”. The board will review and approve or reject the request." },
    ],
  },
  {
    id: "review-works-permit",
    category: "governance",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/resident/works-permit",
    titleEs: "Revisar o aprobar un permiso de obra",
    titleEn: "Review or approve a works permit",
    keywords: [
      "aprobar", "aprobacion", "revisar", "revision", "rechazar", "permiso", "obra",
      "approve", "review", "reject", "permit", "works",
    ],
    whoEs: "Administradores o miembros de la Junta",
    whoEn: "Admins or board members",
    steps: [
      { es: "En el menú lateral, abra «Solicitudes de Titulares».", en: "In the sidebar, open “Works Permits”." },
      { es: "Busque una solicitud con estado «Enviada» y, cuando esté completa, haga clic en «Enviar a Resoluciones».", en: "Find a request with status “Submitted” and, once it is complete, click “Send to Resolutions”." },
      { es: "En «Resoluciones», la tarjeta muestra la solicitud y toda su documentación; haga clic en «Revisar y decidir».", en: "In “Resolutions”, the card shows the request and all its documents; click “Review and decide”." },
      { es: "Elija «Aprobar» o «Rechazar» y agregue notas. La solicitud queda marcada igual en Solicitudes de Titulares.", en: "Choose “Approve” or “Reject” and add notes. The request is marked the same way in Works Permits." },
      { es: "Cuando la resolución esté firmada, súbala con «Subir resolución firmada»; el titular la descargará desde su solicitud.", en: "Once the resolution is signed, upload it with “Upload signed resolution”; the owner downloads it from their request." },
    ],
  },
  {
    id: "record-fee-payment",
    category: "finance",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/finance/fees",
    titleEs: "Registrar el pago de una cuota",
    titleEn: "Record a maintenance-fee payment",
    keywords: [
      "cuota", "cuotas", "pago", "pagar", "deuda", "mantenimiento", "fee", "fees", "payment",
      "pay", "dues", "balance",
    ],
    whoEs: "Administradores o miembros de la Junta",
    whoEn: "Admins or board members",
    steps: [
      { es: "En el menú lateral, abra «Cuotas».", en: "In the sidebar, open “Fees”." },
      { es: "Localice la cuota pendiente del titular.", en: "Locate the owner’s pending fee." },
      { es: "Haga clic en «Registrar Pago», confirme el monto y la fecha, y guarde.", en: "Click “Record Payment”, confirm the amount and date, and save." },
    ],
  },
  {
    id: "create-resolution",
    category: "governance",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/resolutions",
    titleEs: "Crear una resolución y someterla a votación",
    titleEn: "Create a resolution and put it to a vote",
    keywords: [
      "resolucion", "resoluciones", "voto", "votos", "votar", "acuerdo", "decision",
      "resolution", "vote", "voting", "agreement",
    ],
    whoEs: "Administradores o miembros de la Junta",
    whoEn: "Admins or board members",
    steps: [
      { es: "En el menú lateral, abra «Resoluciones».", en: "In the sidebar, open “Resolutions”." },
      { es: "Haga clic en «+ Nueva Resolución» y complete título, descripción y quién la propone.", en: "Click “+ New Resolution” and fill in title, description, and who proposes it." },
      { es: "Guarde. Cada miembro activo de la Junta vota desde su propia cuenta (A favor / En contra / Abstención); la Administración no vota.", en: "Save. Each active board member votes from their own account (Yes / No / Abstain); the Administration does not vote." },
      { es: "Se cierra sola cuando todos han votado. Si alguien no vota, la Administración puede usar «Cerrar votación»: se decide con los votos emitidos.", en: "It closes by itself once everyone has voted. If someone does not vote, the Administration can use “Close voting”: it is decided on the votes cast." },
    ],
  },
  {
    id: "upload-document",
    category: "documents",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/documents",
    titleEs: "Subir un documento oficial",
    titleEn: "Upload an official document",
    keywords: [
      "documento", "documentos", "subir", "cargar", "reglamento", "manual", "acta", "archivo",
      "document", "upload", "rules", "bylaws", "file", "pdf",
    ],
    whoEs: "Administradores o miembros de la Junta",
    whoEn: "Admins or board members",
    steps: [
      { es: "En el menú lateral, abra «Documentos».", en: "In the sidebar, open “Documents”." },
      { es: "Haga clic en «+ Subir Documento».", en: "Click “+ Upload Document”." },
      { es: "Complete: título, tipo, versión, fecha de vigencia y seleccione el archivo.", en: "Fill in: title, type, version, effective date, and select the file." },
      { es: "Haga clic en «Guardar».", en: "Click “Save”." },
    ],
  },
  {
    id: "record-expense",
    category: "finance",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/finance/expenses",
    titleEs: "Registrar un gasto",
    titleEn: "Record an expense",
    keywords: [
      "gasto", "gastos", "factura", "recibo", "costo", "pago", "expense", "expenses",
      "invoice", "receipt", "cost",
    ],
    whoEs: "Administradores o miembros de la Junta",
    whoEn: "Admins or board members",
    steps: [
      { es: "En el menú lateral, abra «Gastos».", en: "In the sidebar, open “Expenses”." },
      { es: "Haga clic en «+ Nuevo Gasto» y complete categoría, monto, descripción y recibo (opcional).", en: "Click “+ New Expense” and fill in category, amount, description, and an optional receipt." },
      { es: "Guarde — el gasto queda registrado para revisión/aprobación.", en: "Save — the expense is recorded for review/approval." },
    ],
  },
  {
    id: "report-incident",
    category: "security",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/admin/incidents",
    titleEs: "Reportar un incidente de seguridad",
    titleEn: "Report a security incident",
    keywords: [
      "incidente", "incidentes", "seguridad", "reporte", "robo", "dano", "vandalismo",
      "incident", "security", "report", "theft", "damage",
    ],
    whoEs: "Administradores o miembros de la Junta",
    whoEn: "Admins or board members",
    steps: [
      { es: "En el menú lateral, abra «Incidentes».", en: "In the sidebar, open “Incidents”." },
      { es: "Haga clic en «+ Nuevo» y complete lugar, tipo, descripción, unidad involucrada (opcional) y fotos.", en: "Click “+ New” and fill in location, type, description, involved unit (optional), and photos." },
      { es: "Haga clic en «Guardar».", en: "Click “Save”." },
    ],
  },
  {
    id: "resident-fees",
    category: "finance",
    roles: ["RESIDENT"],
    route: "/resident/fees",
    titleEs: "Ver y pagar mis cuotas",
    titleEn: "View and pay my fees",
    keywords: [
      "mis cuotas", "cuota", "pagar", "saldo", "deuda", "balance", "my fees", "fee", "pay", "dues",
    ],
    whoEs: "Residentes",
    whoEn: "Residents",
    steps: [
      { es: "Abra «Mis Cuotas» desde su Tablero.", en: "Open “My Fees” from your Dashboard." },
      { es: "Revise el saldo pendiente y el detalle de cada cuota.", en: "Review your pending balance and each fee’s detail." },
    ],
  },
  {
    id: "view-documents",
    category: "documents",
    roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"],
    route: "/documents",
    titleEs: "Ver los documentos del condominio",
    titleEn: "View condominium documents",
    keywords: [
      "documento", "documentos", "reglamento", "manual", "normas", "presupuesto", "ver", "leer",
      "document", "documents", "rules", "bylaws", "view", "read",
    ],
    whoEs: "Todos los usuarios",
    whoEn: "All users",
    steps: [
      { es: "En el menú lateral, abra «Documentos».", en: "In the sidebar, open “Documents”." },
      { es: "Haga clic en «Abrir» junto al documento que desea ver (reglamento, manual, presupuesto, etc.).", en: "Click “Open” next to the document you want (rules, manual, budget, etc.)." },
    ],
  },
  {
    id: "change-language",
    category: "account",
    roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"],
    titleEs: "Cambiar el idioma (Español / English)",
    titleEn: "Change the language (Spanish / English)",
    keywords: [
      "idioma", "lenguaje", "ingles", "espanol", "cambiar idioma", "language", "english",
      "spanish", "translate", "traducir",
    ],
    whoEs: "Todos los usuarios",
    whoEn: "All users",
    steps: [
      { es: "En la barra superior, haga clic en el botón del globo 🌐 que muestra «ES» o «EN».", en: "In the top bar, click the globe button 🌐 showing “ES” or “EN”." },
      { es: "El portal cambia entre español e inglés al instante y recuerda su preferencia.", en: "The portal switches between Spanish and English instantly and remembers your choice." },
    ],
  },
  {
    id: "take-tour",
    category: "account",
    roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"],
    route: "/help/tour",
    titleEs: "Ver el tour guiado / centro de ayuda",
    titleEn: "Take the guided tour / help center",
    keywords: [
      "tour", "ayuda", "guia", "tutorial", "introduccion", "help", "guide", "onboarding", "centro de ayuda",
    ],
    whoEs: "Todos los usuarios",
    whoEn: "All users",
    steps: [
      { es: "Abra «Ayuda» en el menú lateral, o vaya directamente al Tour guiado.", en: "Open “Help” in the sidebar, or go straight to the guided Tour." },
      { es: "Use «Tomar el tour» para repetir la introducción cuando lo desee.", en: "Use “Take the tour” to replay the introduction anytime." },
    ],
  },
  {
    id: "onbehalf-works-permit",
    category: "governance",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/resident/works-permit",
    titleEs: "Registrar un permiso de obra en nombre de un residente",
    titleEn: "Enter a works permit on behalf of a resident",
    keywords: [
      "en nombre", "nombre de", "por el residente", "registrar por", "capturar", "permiso",
      "obra", "unidad", "on behalf", "for a resident", "works permit", "unit", "enter for",
    ],
    whoEs: "Administradores, o directores con el permiso otorgado",
    whoEn: "Admins, or directors who have been granted the permission",
    steps: [
      { es: "En el menú lateral, abra «Solicitudes de Titulares».", en: "In the sidebar, open “Works Permits”." },
      { es: "Haga clic en «+ Nueva Solicitud».", en: "Click “+ New Request”." },
      { es: "En el campo «Unidad», seleccione la unidad del residente.", en: "In the “Unit” field, select the resident’s unit." },
      { es: "Complete tipo de obra, descripción, fechas y contratista; luego «Guardar».", en: "Fill in work type, description, dates, and contractor; then “Save”." },
    ],
    noteEs: "Necesita el permiso «Registrar permisos de obra en nombre de residentes» (los administradores lo tienen siempre). La solicitud queda marcada «En nombre de» y registrada con su nombre en el Registro de Auditoría.",
    noteEn: "You need the “Enter works permits on behalf of residents” permission (admins always have it). The request is tagged “On behalf” and logged under your name in the Audit Log.",
  },
  {
    id: "onbehalf-maintenance",
    category: "resident",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/resident/maintenance",
    titleEs: "Registrar una solicitud de mantenimiento en nombre de un residente",
    titleEn: "Enter a maintenance request on behalf of a resident",
    keywords: [
      "en nombre", "nombre de", "registrar por", "capturar", "mantenimiento", "unidad",
      "on behalf", "for a resident", "maintenance", "unit", "enter for", "residente",
    ],
    whoEs: "Administradores, o directores con el permiso otorgado",
    whoEn: "Admins, or directors who have been granted the permission",
    steps: [
      { es: "En el menú lateral, abra «Mantenimiento».", en: "In the sidebar, open “Maintenance”." },
      { es: "Haga clic en «+ Nueva Solicitud».", en: "Click “+ New Request”." },
      { es: "En «Unidad», elija la unidad del residente (o déjelo en Área común si no aplica).", en: "In “Unit”, choose the resident’s unit (or leave it as Common area if not applicable)." },
      { es: "Complete categoría, título, descripción y prioridad; luego «Guardar».", en: "Fill in category, title, description, and priority; then “Save”." },
    ],
    noteEs: "Necesita el permiso «Registrar mantenimiento en nombre de residentes» (los administradores lo tienen siempre). Queda registrado con su nombre en el Registro de Auditoría.",
    noteEn: "You need the “Enter maintenance on behalf of residents” permission (admins always have it). It’s logged under your name in the Audit Log.",
  },
  {
    id: "grant-permissions",
    category: "account",
    roles: ["ADMIN"],
    route: "/admin/users",
    titleEs: "Autorizar a un director a registrar datos (permisos de captura)",
    titleEn: "Let a director enter data (data-entry permissions)",
    keywords: [
      "permiso", "permisos", "autorizar", "delegar", "director", "directores", "captura",
      "en nombre", "permission", "permissions", "grant", "delegate", "authorize", "checkbox", "users",
    ],
    whoEs: "Solo administradores",
    whoEn: "Admins only",
    steps: [
      { es: "En el menú lateral, abra «Usuarios».", en: "In the sidebar, open “Users”." },
      { es: "Junto al director, haga clic en «Editar».", en: "Next to the director, click “Edit”." },
      { es: "En «Permisos de captura», marque las casillas que desea otorgar (permisos de obra, mantenimiento, incidentes en nombre de residentes).", en: "Under “Data-entry permissions”, check the boxes you want to grant (works permits, maintenance, incidents on behalf of residents)." },
      { es: "Haga clic en «Guardar».", en: "Click “Save”." },
    ],
    noteEs: "Los administradores tienen todos los permisos automáticamente. Cada cambio queda registrado en el Registro de Auditoría. Para quitar un permiso, desmarque la casilla y guarde.",
    noteEn: "Admins have all permissions automatically. Every change is recorded in the Audit Log. To revoke, uncheck the box and save.",
  },
  {
    id: "drive-archive",
    category: "documents",
    roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"],
    titleEs: "¿Dónde se guardan los documentos y contratos? (archivo en Google Drive)",
    titleEn: "Where are documents & contracts archived? (Google Drive)",
    keywords: [
      "google drive", "drive", "archivo", "respaldo", "copia", "nube", "contratos", "documentos",
      "donde se guardan", "backup", "archive", "saved", "storage", "cloud", "record",
    ],
    whoEs: "Informativo — para todos",
    whoEn: "Informational — for everyone",
    steps: [
      { es: "Los documentos y contratos que se suben, y los PDF oficiales (actas, resoluciones, permisos), se copian automáticamente a la carpeta compartida de Google Drive «Documentos oficiales» del condominio.", en: "Uploaded documents and contracts, plus official PDFs (minutes, resolutions, permits), are automatically copied to the condo’s shared Google Drive folder “Documentos oficiales.”" },
      { es: "La copia ocurre sola, cada hora. No hay que hacer nada.", en: "The copy happens automatically, every hour. Nothing to do." },
      { es: "Para consultar el archivo, abra el Google Drive del condominio (unidad compartida «Costa Dorada — Archivo»).", en: "To browse the archive, open the condo’s Google Drive (shared drive “Costa Dorada — Archivo”)." },
    ],
    noteEs: "Es una copia de respaldo (solo lectura para conservación). El portal sigue mostrando y descargando los archivos con normalidad.",
    noteEn: "It’s a backup copy for record-keeping. The portal still shows and downloads files as usual.",
  },
  {
    id: "consult-rules",
    category: "documents",
    roles: ["ADMIN", "BOARD_MEMBER", "RESIDENT"],
    titleEs: "Consultar una regla o norma del condominio (multas, permisos, asamblea, mascotas…)",
    titleEn: "Look up a condo rule (fines, permits, assembly, pets…)",
    keywords: [
      "regla", "reglas", "norma", "normas", "reglamento", "manual", "multa", "cita", "articulo",
      "que dice", "rule", "rules", "regulation", "fine", "citation", "article", "consultar", "buscar",
    ],
    whoEs: "Informativo — para todos",
    whoEn: "Informational — for everyone",
    steps: [
      { es: "Haga clic en el botón «Preguntar» (abajo a la derecha, en cualquier pantalla).", en: "Click the “Ask” button (bottom-right, on any screen)." },
      { es: "Escriba su pregunta en español o inglés, por ejemplo «¿cuánto es la multa?» o «¿qué necesito para instalar rejas?».", en: "Type your question in Spanish or English, e.g. “what is the fine?” or “what do I need to install security bars?”." },
      { es: "En la sección «Reglas y normas» verá la regla en lenguaje sencillo, con su cita (ej. «Reglamento Art. VI Sec. 2(T)») y un enlace «Ver documento».", en: "Under “Rules & regulations” you’ll see the rule in plain language, with its citation (e.g. “Reglamento Art. VI Sec. 2(T)”) and a “View document” link." },
    ],
    noteEs: "Las reglas también aparecen automáticamente en los formularios donde se toman decisiones (permisos de obra, incidentes, cuotas, asambleas) como panel «Reglas aplicables».",
    noteEn: "Rules also appear automatically as an “Applicable rules” panel inside the decision forms (works permits, incidents, fees, meetings).",
  },
  {
    id: "manage-governing-documents",
    category: "documents",
    roles: ["ADMIN", "BOARD_MEMBER"],
    route: "/documents",
    titleEs: "Subir la Escritura, el Plan de Emergencias u otro documento oficial",
    titleEn: "Upload the Master Deed, Emergency Plan, or other official document",
    keywords: [
      "escritura", "matriz", "plan de emergencias", "racionamiento", "seguro", "poliza", "documento",
      "subir", "deed", "emergency plan", "insurance", "policy", "upload", "reglamento", "manual",
    ],
    whoEs: "Junta y administradores",
    whoEn: "Board and admins",
    steps: [
      { es: "En el menú lateral, abra «Biblioteca de Documentos».", en: "In the sidebar, open “Document Library”." },
      { es: "Haga clic en «Subir Documento» y elija el tipo correcto (Escritura Matriz, Plan de Emergencias, Plan de Racionamiento, Póliza de Seguro, etc.).", en: "Click “Upload Document” and pick the correct type (Master Deed, Emergency Plan, Rationing Plan, Insurance Policy, etc.)." },
      { es: "Suba el archivo (o elíjalo desde Google Drive), indique la versión y guarde.", en: "Upload the file (or pick it from Google Drive), set the version, and save." },
    ],
    noteEs: "La Escritura, el Reglamento, el Manual, el Plan de Emergencias y el Plan de Racionamiento son visibles para los titulares. La Póliza de Seguro queda visible solo para Junta y administración.",
    noteEn: "The Deed, Bylaws, Rules Manual, Emergency Plan, and Rationing Plan are owner-visible. The Insurance Policy is visible only to the board and administration.",
  },
];
